import { Portal as TooltipPortal } from '@radix-ui/react-tooltip';
import { Copy, CopyPlus, Edit, Lock, Mail, Phone, RefreshCw, Star, Trash } from 'lucide-react';
import type { ReactElement } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import type { LocalContact, LocalContactItem } from '@/context/create-account-flow';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

interface ContactCardProps {
  contact: LocalContact;
  onEdit: () => void;
  onDelete: () => void;
  onCopy?: () => void;
  onDuplicate?: () => void;
  canEdit?: boolean;
}

const getContactIcons = (
  type: LocalContactItem['type'],
  preferred: LocalContactItem['preferredContact'] | undefined,
) => {
  const icons: ReactElement[] = [];
  const isWhatsapp = !!preferred?.whatsapp;
  const isPhone = !!preferred?.phone;
  const isEmail = !!preferred?.email;

  if (type === 'Celular') {
    if (isWhatsapp) {
      icons.push(
        <svg
          key="whatsapp"
          className={cn('h-4 w-4', isWhatsapp ? 'text-primary' : 'text-muted-foreground')}
          viewBox="0 0 24 24"
          fill="currentColor"
        >
          <title>WhatsApp</title>
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
        </svg>,
      );
    }
    if (isPhone) {
      icons.push(
        <Phone
          key="phone"
          className={cn('h-4 w-4', isPhone ? 'text-primary' : 'text-muted-foreground')}
        />,
      );
    }
    if (!isWhatsapp && !isPhone) {
      icons.push(<Phone key="phone-default" className="h-4 w-4 text-muted-foreground" />);
    }
  } else if (type === 'Telefone Residencial' || type === 'Telefone Comercial') {
    icons.push(
      <Phone
        key="phone"
        className={cn('h-4 w-4', isPhone ? 'text-primary' : 'text-muted-foreground')}
      />,
    );
  } else if (type === 'Email') {
    icons.push(
      <Mail
        key="email"
        className={cn('h-4 w-4', isEmail ? 'text-primary' : 'text-muted-foreground')}
      />,
    );
  } else {
    icons.push(<Phone key="phone-fallback" className="h-4 w-4 text-muted-foreground" />);
  }

  return icons.map((icon) => {
    const label =
      icon.key === 'whatsapp' && isWhatsapp
        ? 'WhatsApp preferido'
        : icon.key === 'phone' && isPhone
          ? 'Ligação preferida'
          : icon.key === 'email' && isEmail
            ? 'E-mail preferido'
            : undefined;

    return label ? (
      <TooltipProvider key={icon.key}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-label={label}
              className="inline-flex cursor-pointer items-center gap-0.5 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {icon}
              <Star className="h-3 w-3 text-primary fill-current" aria-hidden="true" />
            </button>
          </TooltipTrigger>
          <TooltipPortal>
            <TooltipContent>Preferência de contato</TooltipContent>
          </TooltipPortal>
        </Tooltip>
      </TooltipProvider>
    ) : (
      icon
    );
  });
};

const formatContactValue = (type: LocalContactItem['type'], value: string) => {
  if (type === 'Celular' || type === 'Telefone Residencial' || type === 'Telefone Comercial') {
    return value.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  }
  return value;
};

export function ContactCard({
  contact,
  onEdit,
  onDelete,
  onCopy,
  onDuplicate,
  canEdit = true,
}: ContactCardProps) {
  const { toast } = useToast();
  const isFiscal = contact.contactFor?.includes('Fiscal');

  const handleCopy = () => {
    const contactInfo =
      `${contact.name}\n` +
      (contact.contactItems || []).map((item) => `${item.type}: ${item.contact}`).join('\n');

    if (onCopy) {
      onCopy();
    } else {
      navigator.clipboard.writeText(contactInfo);
      toast({
        variant: 'success',
        title: 'Copiado!',
        description: 'Contato copiado para a area de transferencia.',
      });
    }
  };

  return (
    <Card className="w-full max-w-xs flex flex-col mx-0 gap-0 rounded-lg border bg-card overflow-hidden">
      {/* Top bar with action buttons */}
      <div className="flex items-center justify-end gap-0.5 px-2 py-1 bg-muted/40 border-b">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                type="button"
                className="h-6 w-6 text-muted-foreground hover:text-foreground"
                onClick={handleCopy}
                aria-label="Copiar Contato"
              >
                <Copy className="h-3.5 w-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top">
              <p>Copiar</p>
            </TooltipContent>
          </Tooltip>

          {canEdit && isFiscal && onDuplicate && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  type="button"
                  className="h-6 w-6 text-muted-foreground hover:text-foreground"
                  onClick={onDuplicate}
                  aria-label="Duplicar Contato"
                >
                  <CopyPlus className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top">
                <p>Duplicar</p>
              </TooltipContent>
            </Tooltip>
          )}

          {contact.originType === 'api' && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  type="button"
                  className="h-6 w-6 text-muted-foreground hover:text-foreground"
                  aria-label="Atualizar Contato"
                >
                  <RefreshCw className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top">
                <p>Atualizar</p>
              </TooltipContent>
            </Tooltip>
          )}

          {canEdit && !isFiscal && contact.originType !== 'api' && (
            <>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    type="button"
                    className="h-6 w-6 text-muted-foreground hover:text-foreground"
                    onClick={onEdit}
                    aria-label="Editar Contato"
                  >
                    <Edit className="h-3.5 w-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top">
                  <p>Editar</p>
                </TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    type="button"
                    className="h-6 w-6 text-muted-foreground hover:text-red-500"
                    onClick={onDelete}
                    aria-label="Excluir Contato"
                  >
                    <Trash className="h-3.5 w-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top">
                  <p>Excluir</p>
                </TooltipContent>
              </Tooltip>
            </>
          )}
        </TooltipProvider>
      </div>

      {/* Card content */}
      <div className="p-4 space-y-2">
        <div className="space-y-1">
          <h3 title={contact.name} className="text-sm font-semibold text-foreground leading-tight">
            {contact.name}
          </h3>
          {contact.positionOrRelation && (
            <p className="text-xs text-muted-foreground">{contact.positionOrRelation}</p>
          )}
        </div>

        <div className="flex gap-1.5 flex-wrap">
          {contact.contractEnabled && (
            <Badge variant="outline" className="text-xs">
              Contrato
            </Badge>
          )}
          {contact.contactFor.map((forItem) => (
            <Badge key={forItem} variant="outline" className="text-xs">
              {forItem}
            </Badge>
          ))}
          {isFiscal && (
            <Badge variant="secondary" className="text-xs font-medium gap-1">
              <Lock className="h-3 w-3" />
              Dados da Receita
            </Badge>
          )}
        </div>

        <div className="space-y-1">
          {contact.contactItems.map((item) => {
            const isPreferred =
              (item.type === 'Celular' &&
                (item.preferredContact?.whatsapp || item.preferredContact?.phone)) ||
              ((item.type === 'Telefone Residencial' || item.type === 'Telefone Comercial') &&
                item.preferredContact?.phone) ||
              (item.type === 'Email' && item.preferredContact?.email);

            return (
              <div key={item.id} className="flex items-center gap-2 text-sm my-auto">
                <div className="flex items-center gap-1">
                  {getContactIcons(item.type, item.preferredContact)}
                </div>
                <span
                  className={cn(
                    'min-w-0 break-words whitespace-normal text-xs',
                    isPreferred ? 'font-medium text-foreground' : 'text-muted-foreground',
                  )}
                >
                  {formatContactValue(item.type, item.contact)}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </Card>
  );
}
